import { Injectable, Injector } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { StorageService } from 'src/app/_services/storage.service';
import { AuthService } from 'src/app/account/auth/_services/auth.service';

/** Marks a request already retried after a renewal, so it is never renewed twice. */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

/** The stored session changed (logout or another login) while a renewal was in flight. */
class SessionChangedError extends Error {}

/**
 * Renews the session token when a request sent with it to a platform API gets a 401,
 * then retries the request once with the new token. Concurrent 401s share one renewal;
 * if the renewal is rejected the session is closed, the user is sent to the login screen and
 * the pending requests end without error, so screens don't cover the notice with their own.
 * A renewal never overwrites a session that changed meanwhile, and a request is only
 * retried with the token that replaced the one it was sent with.
 * Must be registered after AuthInterceptor so it sees the bearer that one adds.
 */
@Injectable()
export class SessionRefreshInterceptor implements HttpInterceptor {
  private readonly http: HttpClient;
  private refreshInFlight$: Observable<string> | null = null;
  /** Last completed renewal: the token it replaced and the one it issued. */
  private lastRenewal: { from: string; to: string } | null = null;

  constructor(
    httpBackend: HttpBackend,
    private storageService: StorageService,
    private injector: Injector,
  ) {
    // Bypasses the interceptor chain so the renewal call is never intercepted itself.
    this.http = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!this.isRenewable(req, error)) {
          return throwError(error);
        }
        return this.currentOrRenewedToken(req).pipe(
          switchMap((token) => next.handle(req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            context: req.context.set(RETRIED_AFTER_REFRESH, true),
          }))),
        );
      }),
    );
  }

  private isRenewable(req: HttpRequest<any>, error: any): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) return false;
    if (req.context.get(RETRIED_AFTER_REFRESH)) return false;
    if (!this.isPlatformApi(req.url)) return false;
    const sentToken = this.bearerOf(req);
    return !!sentToken && (sentToken === this.sessionToken() || this.wasRenewedToCurrent(sentToken));
  }

  /** A request sent with a token already replaced by a renewal just uses the current one. */
  private currentOrRenewedToken(req: HttpRequest<any>): Observable<string> {
    const sentToken = this.bearerOf(req);
    if (sentToken && this.wasRenewedToCurrent(sentToken)) {
      return of(this.sessionToken() as string);
    }
    return this.renewToken();
  }

  /** True when the last renewal replaced `token` and its result is still the stored session. */
  private wasRenewedToCurrent(token: string): boolean {
    return !!this.lastRenewal && this.lastRenewal.from === token && this.lastRenewal.to === this.sessionToken();
  }

  private isPlatformApi(url: string): boolean {
    return Object.values(environment.microservices)
      .some((base) => typeof base === 'string' && url.startsWith(base));
  }

  private renewToken(): Observable<string> {
    if (!this.refreshInFlight$) {
      const session = this.storageService.getItem('currentUser');
      const renewedFrom: string | undefined = session?.token;
      this.refreshInFlight$ = this.http
        .post<{ token: string }>(`${environment.microservices.user}auth/refresh-token`, { token: renewedFrom })
        .pipe(
          map((res) => {
            if (!res?.token) throw new Error('Refresh response without token');
            if (!renewedFrom || this.sessionToken() !== renewedFrom) throw new SessionChangedError();
            this.lastRenewal = { from: renewedFrom, to: res.token };
            this.storageService.setItem('currentUser', { ...session, token: res.token });
            return res.token;
          }),
          catchError((error) => {
            if (error instanceof SessionChangedError) return throwError(error);
            this.expireSession();
            return EMPTY;
          }),
          finalize(() => (this.refreshInFlight$ = null)),
          shareReplay(1),
        );
    }
    return this.refreshInFlight$;
  }

  private expireSession(): void {
    this.injector.get(AuthService).logout();
    Swal.fire({
      icon: 'info',
      title: 'Tu sesión expiró',
      text: 'Por favor, vuelve a iniciar sesión.',
    });
  }

  private sessionToken(): string | null {
    return this.storageService.getItem('currentUser')?.token ?? null;
  }

  private bearerOf(req: HttpRequest<any>): string | null {
    const header = req.headers.get('Authorization');
    return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
  }
}
